'use no memo';

import type { GroupPublic } from '@repo/types/groups';
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';

import { useGetGroups } from '@repo/api/group/useGetGroups';
import { cn } from '@repo/lib/cn';
import { countStudents } from '@repo/lib/user-lvl/countStudents';
import { format, startOfDay, endOfDay } from 'date-fns';
import { EditIcon, PlusIcon, Trash2Icon, UserIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import CreateGroup from '@/components/features/manage/manage-groups/CreateGroup';
import DeleteGroup from '@/components/features/manage/manage-groups/DeleteGroup';
import EditGroup from '@/components/features/manage/manage-groups/EditGroup';
import EditGroupSchedule from '@/components/features/manage/manage-groups/EditGroupSchedule';
import EditGroupUsers from '@/components/features/manage/manage-groups/EditGroupUsers';
import GroupAvatarEditor from '@/components/features/manage/manage-groups/GroupAvatarEditor';
import TanstackTable from '@/components/features/manage/tanstack-table/TanstackTable';
import { useTanstackTable } from '@/components/features/manage/tanstack-table/useTanstackTable';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useTableRowSelection } from '@/hooks/table';
import { getAvatar } from '@/utils/getAvatar';

export default function ManageGroupsPage() {
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openEditAvatar, setOpenEditAvatar] = useState<boolean>(false);
  const [openEditUsers, setOpenEditUsers] = useState<boolean>(false);
  const [openEditSchedule, setOpenEditSchedule] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openGroupAdd, setOpenGroupAdd] = useState<boolean>(false);

  const [activeGroup, setActiveGroup] = useState<GroupPublic | undefined>(undefined);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data = [], isPending } = useGetGroups();
  const columns = useMemo<ColumnDef<GroupPublic>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected() ? true : table.getIsSomePageRowsSelected() ? 'indeterminate' : false}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label={'Select row'} />,
        enableSorting: false,
        enableHiding: false,
      },
      {
        meta: 'Awatar',
        id: 'avatarURL',
        accessorKey: 'avatarURL',
        cell: ({ getValue }) => {
          const avatarURL = getValue();
          const url = typeof avatarURL === 'string' ? getAvatar(avatarURL) : undefined;
          return (
            <Avatar size={'sm'}>
              <AvatarImage src={url} />
              <AvatarFallback>
                <UserIcon className="size-3.5" />
              </AvatarFallback>
            </Avatar>
          );
        },
        enableSorting: false,
        header: 'Awatar',
      },
      {
        meta: 'Nazwa',
        id: 'name',
        accessorKey: 'name',
        header: 'Nazwa',
      },
      {
        meta: 'Opis',
        id: 'desc',
        accessorKey: 'desc',
        header: 'Opis',
        cell: ({ getValue }) => {
          const desc = getValue();
          if (!desc) {
            return <p>-</p>;
          }
          return desc;
        },
      },
      {
        meta: 'Liczba miejsc',
        id: 'slots',
        accessorKey: 'slots',
        header: 'Liczba miejsc',
        type: 'number',
        accessorFn: (row) => (row.slots && row.users ? row.slots / countStudents(row.users) : null),
        cell: ({ row }) => {
          return (
            <p className={cn(row.original.slots && countStudents(row.original.users) > row.original.slots && 'text-text-warning')}>
              {row.original.slots ? `${countStudents(row.original.users)} / ${row.original.slots}` : '-'}
            </p>
          );
        },
      },
      {
        meta: 'Stworzono',
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Stworzono',
        cell: ({ getValue }) => {
          const createdAt = getValue();

          return format(createdAt as string, 'HH:mm dd.MM.yyyy');
        },
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string;
          if (!dateStr) return false;
          const date = startOfDay(new Date(dateStr));
          const start = value.start ? startOfDay(new Date(value.start)) : null;
          const end = value.end ? endOfDay(new Date(value.end)) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const rowOrigin = row.original;
          return (
            <div className={'flex w-full justify-end'}>
              <Button
                size={'sm'}
                variant={'ghost'}
                variantColor={'destructive'}
                onClick={() => {
                  setActiveGroup(rowOrigin);
                  setOpenDelete(true);
                }}
              >
                <Trash2Icon />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size={'sm'} variant={'ghost'}>
                    <EditIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={'end'} className="w-full max-w-xs">
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveGroup(rowOrigin);
                        setOpenEdit(true);
                      }}
                    >
                      <div className="space-y-0.5">
                        <p>Edytuj</p>
                        <p className="text-text-tertiary ms-1 text-xs">Edytuj dane grupy</p>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveGroup(rowOrigin);
                        setOpenEditUsers(true);
                      }}
                    >
                      <div className="space-y-0.5">
                        <p>Zmień użytkowników</p>
                        <p className="text-text-tertiary ms-1 text-xs">Edytuj użytkowników grupy</p>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveGroup(rowOrigin);
                        setOpenEditSchedule(true);
                      }}
                    >
                      <div className="space-y-0.5">
                        <p>Edytuj harmonogram</p>
                        <p className="text-text-tertiary ms-1 text-xs">Edytuj harmonogram grupy</p>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveGroup(rowOrigin);
                        setOpenEditAvatar(true);
                      }}
                    >
                      <div className="space-y-0.5">
                        <p>Zmień awatar</p>
                        <p className="text-text-tertiary ms-1 text-xs">Edytuj awatar grupy</p>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    []
  );

  const { table, setGlobalFilter } = useTanstackTable({
    name: 'groups',
    columns,
    data,
    getRowId: (row) => String(row.id),
    rowSelection: rowSelection,
    onRowSelectionChange: setRowSelection,
    defaultColumnVisibility: {},
  });

  const selectedGroups = useMemo<GroupPublic[]>(() => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return [];
    const idSet = new Set(selectedIds.map(Number));
    return data.filter((group) => idSet.has(group.id));
  }, [rowSelection, data]);

  const { handleRowClick } = useTableRowSelection({
    table,
    setRowSelection,
  });

  const addGroup = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Dodaj Grupę',
        icon: <PlusIcon className="size-4" />,
        func: () => setOpenGroupAdd(true),
      },
    ];
  }, []);

  return (
    <>
      <CreateGroup open={openGroupAdd} onOpenChange={setOpenGroupAdd} />
      <EditGroup
        id={activeGroup?.id || selectedGroups?.[0]?.id}
        open={openEdit}
        onOpenChange={(open) => {
          setOpenEdit(open);
          if (!open) setActiveGroup(undefined);
        }}
      />
      <EditGroupUsers
        id={activeGroup?.id || selectedGroups?.[0]?.id}
        open={openEditUsers}
        onOpenChange={(open) => {
          setOpenEditUsers(open);
          if (!open) setActiveGroup(undefined);
        }}
      />
      <GroupAvatarEditor
        id={activeGroup?.id || selectedGroups?.[0]?.id}
        open={openEditAvatar}
        onOpenChange={(open) => {
          setOpenEditAvatar(open);
          if (!open) setActiveGroup(undefined);
        }}
      />
      <EditGroupSchedule
        id={activeGroup?.id || selectedGroups?.[0]?.id}
        open={openEditSchedule}
        onOpenChange={(open) => {
          setOpenEditSchedule(open);
          if (!open) setActiveGroup(undefined);
        }}
      />
      <DeleteGroup
        groups={activeGroup ? [activeGroup] : selectedGroups}
        open={openDelete}
        onOpenChange={(open) => {
          setOpenDelete(open);
          if (!open) setActiveGroup(undefined);
        }}
      />
      <div className={'base-direction-col h-full'}>
        <div className={'viewport-height flex w-full flex-col gap-4'}>
          <TanstackTable
            table={table}
            label={'Grupy'}
            isLoading={isPending}
            onGlobalFilterChange={setGlobalFilter}
            filterBy={[
              {
                id: 'createdAt',
                title: 'Data utworzenia',
                type: 'dateRange',
              },
            ]}
            onRemoveSelectedRows={() => setOpenDelete(true)}
            addGroup={addGroup}
            onRowClick={handleRowClick}
          />
        </div>
      </div>
    </>
  );
}
